import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';

export class ProfileElementRef extends DataModel implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = 'b914c85a97924007a9d9561ef0318497';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly refId: string;

  constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly elementId: string,
    public readonly name: string,
  ) {
    super();
    this.refId = `${this.businessRef.encoded}:${this.elementId}`;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(ProfileElementRef.SCHEMA_VERSION);
  }

  get $typeId(): string {
    return ProfileElementRef.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProfileElementRef {
    return new ProfileElementRef(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'elementId'),
        MapUtils.get<string>(map, 'name'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'elementId': this.elementId,
      'name': this.name,
    };
  }

  clone(): this {
    return new ProfileElementRef(
        this.businessRef.clone(),
        this.elementId,
        this.name,
    ) as this;
  }

  isSame(other: ProfileElementRef): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
          other.elementId === this.elementId &&
          other.name === this.name;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new ProfileElementRef(
        businessRef.clone(),
        this.elementId,
        this.name,
    ) as this;
  }
}

export class ProfileElementRefBuilder extends DataModelBuilder<ProfileElementRef> {
  build(context: SerializationContext, map: SerializedData): ProfileElementRef {
    return ProfileElementRef.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ProfileElementRef.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ProfileElementRef.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [businessId] was replaced with [businessRef]
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
